import { useState, useEffect, useCallback, useMemo } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  toApiParams,
  type RenovationDocumentsFilters,
} from "./useRenovationDocumentsData";

/**
 * One reconciliation bucket: a count of invoices and their total value (excl VAT).
 */
export interface ReconciliationBucket {
  count: number;
  value: number;
}

export interface ImportReconciliation {
  /** Reeds geïmporteerd, **binnen de actieve filters** (gebouw, jaar, status, ...). */
  imported: ReconciliationBucket;
  /** Te importeren; scoopt mee op het gebouw wanneer er een gekozen is. */
  toImport: ReconciliationBucket;
  /**
   * Geboekt op een renovatierekening maar zonder kostenplaats. **Altijd globaal**: zonder
   * analytische rekening is er geen gebouw om op te filteren.
   */
  withoutKostenplaats: ReconciliationBucket;
  /**
   * Dezelfde drie emmers **zonder enige filter**. Hierop staat de rekensom, zodat die
   * altijd optelt — met een gebouwfilter actief zou je anders twee gefilterde bedragen bij
   * één globaal bedrag optellen, en dan klopt de som met niets.
   */
  global: {
    imported: ReconciliationBucket;
    toImport: ReconciliationBucket;
    withoutKostenplaats: ReconciliationBucket;
  };
  /**
   * Of de Odoo-cijfers (`toImport`, `withoutKostenplaats`) effectief berekend zijn.
   * Bij `false` zijn ze **onbekend, niet nul** — de kaarten tonen dan geen € 0,00.
   * `imported` komt live uit de databank en klopt altijd.
   */
  analyticsReady: boolean;
  /** Of er een gebouw- of documentfilter actief is. */
  hasScope: boolean;
  scopedBuildingId: string | null;
  /** GL (grootboek) account codes that define a "renovation invoice". */
  ledgerAccounts: string[];
  /** When the underlying cached analytics were last refreshed (Brussels time). */
  lastUpdated: string | null;
}

const EMPTY_BUCKET: ReconciliationBucket = { count: 0, value: 0 };
// Stable default so callers that omit filters don't trigger refetch loops.
const EMPTY_FILTERS: RenovationDocumentsFilters = {};

/**
 * Fetches the import reconciliation totals (Odoo accounting vs YallyHub).
 *
 * The "reeds geïmporteerd" bucket is computed server-side with the same filters
 * as the documents list, so it narrows with the active filters and shows the
 * full total when no filters are set. The other two buckets are global.
 *
 * @param buildingId  Current building scope (optional).
 * @param filters     Active document filters (same shape as useRenovationDocumentsData).
 */
export function useImportReconciliation(
  buildingId?: string,
  filters: RenovationDocumentsFilters = EMPTY_FILTERS,
): {
  data: ImportReconciliation | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
} {
  const { session } = useSession();

  const [data, setData] = useState<ImportReconciliation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const filterParams = useMemo(() => toApiParams(filters), [filters]);
  // Stable key so the fetch only re-runs when the building or a filter changes.
  const queryKey = useMemo(
    () => JSON.stringify({ ...filterParams, __buildingId: buildingId || "" }),
    [filterParams, buildingId],
  );

  const fetchReconciliation = useCallback(async () => {
    if (!session) return;

    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams(filterParams);
      if (buildingId) params.set("buildingId", buildingId);
      const qs = params.toString();
      const url = qs
        ? `${API_URL}/renovation/import-reconciliation?${qs}`
        : `${API_URL}/renovation/import-reconciliation`;

      const response = await fetchWithAuth(url, { method: "GET" }, session);

      if (!response.ok) {
        const errorText = await response
          .text()
          .catch(() => response.statusText);
        throw new Error(
          `Fout bij ophalen van reconciliatie (${response.status}): ${errorText}`,
        );
      }

      const json = await response.json();
      const imported = json.imported ?? EMPTY_BUCKET;
      const toImport = json.toImport ?? EMPTY_BUCKET;
      const withoutKostenplaats = json.withoutKostenplaats ?? EMPTY_BUCKET;
      setData({
        imported,
        toImport,
        withoutKostenplaats,
        // Terugval op de gefilterde cijfers voor een backend van vóór het `global`-blok:
        // dan leest de rekensom hetzelfde als voorheen i.p.v. op nul te staan.
        global: {
          imported: json.global?.imported ?? imported,
          toImport: json.global?.toImport ?? toImport,
          withoutKostenplaats: json.global?.withoutKostenplaats ?? withoutKostenplaats,
        },
        // Een backend van vóór dit veld leverde altijd berekende cijfers zodra ze
        // er waren, dus dan `true`: anders zou elke kaart "nog niet berekend" zeggen.
        analyticsReady: json.analyticsReady ?? true,
        hasScope: json.hasScope ?? false,
        scopedBuildingId: json.scopedBuildingId ?? null,
        ledgerAccounts: json.ledgerAccounts ?? [],
        lastUpdated: json.lastUpdated ?? null,
      });
    } catch (err) {
      const typedError = err instanceof Error ? err : new Error(String(err));
      // Log so Sentry captures the breadcrumb.
      console.error(
        "[useImportReconciliation] Error fetching reconciliation:",
        typedError,
      );
      setError(typedError);
      toast.error("Fout bij ophalen van factuurreconciliatie");
    } finally {
      setIsLoading(false);
    }
    // queryKey captures filterParams + buildingId; both are read inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, queryKey]);

  useEffect(() => {
    if (!session) return;
    fetchReconciliation();
  }, [session, fetchReconciliation]);

  return { data, isLoading, error, refetch: fetchReconciliation };
}
